/**
 * Normalizes the raw API response for room and SKU details into a structure suitable for UI components.
 * @param {Object} apiResponse - Raw API response from getSelectedRoomAndSKUDetails
 * @return {Object} Normalized room and SKU details
 */
export const normalizeRoomAndSkuDetailsResponse = apiResponse => {
  const { fusion: { skuAttributes = [] } = {}, response: { docs = [] } = {} } =
    apiResponse || {}
  const doc = docs[0] || {}
  return {
    skuList: skuAttributes, // Array of SKU attribute objects
    roomTitle: doc.RoomTitle_s || "",
    roomDescription: doc.RoomDescription_s || "",
    roomImageUrl: doc["RoomImage_s"] || "",
  }
}

/**
 * Merges inventory data into SKU attributes for client-side hydration.
 * @param {Array} skuList - Array of SKU attribute objects
 * @param {Array} inventoryList - Array of inventory objects (from inventory API)
 * @param {Array} ctSkusPrices - Array of objects contains prices of specific sku
 * @return {Array} SKU attributes with prices, inventory and image data merged in
 */
export const mergeInventoryWithSkusAndPrices = (
  skuList,
  inventoryList,
  ctSkusPrices
) => {
  return skuList.map(sku => {
    const newSkuObj = { ...sku }
    const inventoryMatch = inventoryList.find(inv => inv.sku === sku.sku_s)
    const skuPriceData = ctSkusPrices.find(inv => inv.sku === sku.sku_s)
    if (skuPriceData) {
      newSkuObj["price"] = skuPriceData.price
      newSkuObj["listPrice"] = skuPriceData.listPrice
      newSkuObj["discountedPrice"] = skuPriceData.discountedPrice
    }
    const imageData = (sku["productImages.labelWithUrl_ss"] || []).find(
      imgs => imgs.includes(sku.sku_s) && imgs.includes("IMGITEMISO")
    )
    const imageUrl = imageData ? imageData.split("|")[5] : null
    if (inventoryMatch) {
      return {
        ...newSkuObj,
        imageUrl,
        availableQuantity: inventoryMatch.availableQuantity,
        currentInventory: inventoryMatch.availableQuantity,
        stock: inventoryMatch.availableQuantity,
        itemQuantity: 1,
        showMsg: false,
        loading: false,
      }
    }
    return { ...newSkuObj, imageUrl }
  })
}

/**
 * Normalizes the raw API response for SKU prices into a structure suitable for UI components.
 * @param {Object} ctSkusData - Raw API response from getProductsBySku
 * @param {string} currencyCode - The currency code for pricing.
 * @param {string} persona - The persona type.
 * @param {Array<string>} skuKeys - The list of SKU keys.
 * @return {Object} Normalized prices for each sku based on currencyCode, persona
 */
export const normalizeSkuPricesResponse = (
  ctSkusData,
  currencyCode,
  persona,
  skuKeys
) => {
  const ctSkusPrices = []
  skuKeys.forEach(sku => {
    let productPrices = {}
    // in case sku matches with masterVariant
    productPrices = ctSkusData.find(ctSku => sku === ctSku?.masterVariant?.sku)
      ?.masterVariant?.prices
    if (!productPrices) {
      // in case sku not matches with masterVariant means matches with other varients
      productPrices = ctSkusData
        .find(ctSku => ctSku.variants.some(variant => variant.sku === sku))
        ?.variants.find(variant => variant.sku === sku)?.prices
    }
    const ctSkuPrice = fetchProductPrices(
      productPrices,
      currencyCode,
      persona,
      sku
    )
    ctSkusPrices.push(ctSkuPrice)
  })
  return ctSkusPrices
}

const fetchProductPrices = (prices, currencyCode, persona, sku) => {
  // persona price
  let priceObj = prices?.find(
    price =>
      price?.value?.currencyCode === currencyCode &&
      price?.customerGroup?.obj?.key === persona
  )

  if (!priceObj) {
    // if persona price not found, then check "ANY" - persona
    priceObj = prices?.find(
      price => price?.value?.currencyCode === currencyCode
    )
  }

  // list price
  const listPriceObj = prices?.find(
    price => price?.value?.currencyCode === currencyCode && !price.customerGroup
  )
  return {
    sku: sku,
    price: ((priceObj?.value?.centAmount ?? 0) / 100).toFixed(2),
    listPrice: ((listPriceObj?.value?.centAmount ?? 0) / 100).toFixed(2),
    discountedPrice: (
      (priceObj?.discounted?.value?.centAmount ?? 0) / 100
    ).toFixed(2),
  }
}
